import { api } from ".";
import {
  IPatientBalanceCommitResult,
  IPatientBalanceUploadDetail,
  IPatientBalanceUploadListItem,
  IPatientBalanceUploadRow,
  PatientBalanceUploadRowStatus,
} from "../../types/patientBalanceUpload";

type ListResponse = {
  data: IPatientBalanceUploadListItem[];
  message?: string;
};

type DetailResponse = {
  data: IPatientBalanceUploadDetail;
  message?: string;
};

type RowResponse = {
  data: IPatientBalanceUploadRow;
  message?: string;
};

type CommitResponse = {
  data: IPatientBalanceCommitResult;
  message?: string;
};

const patientBalanceUploadQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getPatientBalanceUploads: builder.query<
      ListResponse,
      { accountId?: string }
    >({
      query: (params) => ({ url: "/patient-balance-uploads", params }),
      providesTags: ["PatientBalanceUploads"],
    }),
    getPatientBalanceUploadById: builder.query<DetailResponse, string>({
      query: (id) => `/patient-balance-uploads/${id}`,
      providesTags: ["PatientBalanceUploads"],
    }),
    /**
     * The account is required, not optional: its PR discount changes every balance and therefore
     * every payment plan shown in the review step, so it has to be known before parsing.
     */
    createPatientBalanceUpload: builder.mutation<
      DetailResponse,
      { file: File; accountId: string }
    >({
      query: ({ file, accountId }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("accountId", accountId);
        return {
          url: "/patient-balance-uploads",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["PatientBalanceUploads"],
    }),
    updatePatientBalanceUploadRow: builder.mutation<
      RowResponse,
      {
        uploadId: string;
        rowId: string;
        months?: number;
        status?: PatientBalanceUploadRowStatus;
      }
    >({
      query: ({ uploadId, rowId, ...body }) => ({
        url: `/patient-balance-uploads/${uploadId}/rows/${rowId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["PatientBalanceUploads"],
    }),
    /** Creates the live orders and texts the patients. Only succeeds once per upload. */
    commitPatientBalanceUpload: builder.mutation<CommitResponse, string>({
      query: (id) => ({
        url: `/patient-balance-uploads/${id}/commit`,
        method: "POST",
      }),
      invalidatesTags: ["PatientBalanceUploads"],
    }),
  }),
});

export const {
  useGetPatientBalanceUploadsQuery,
  useGetPatientBalanceUploadByIdQuery,
  useCreatePatientBalanceUploadMutation,
  useUpdatePatientBalanceUploadRowMutation,
  useCommitPatientBalanceUploadMutation,
} = patientBalanceUploadQueries;

export default patientBalanceUploadQueries;
